import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import useExperienceSearchLabels from 'components/Experiences/hooks/useExperienceSearchLabels'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import noop from 'lib/function/noop'
import type { ComponentProps } from 'react'
import React, { useCallback, useContext } from 'react'
import ExperiencesSearchDateSelectInline from './ExperiencesSearchDateSelectInline'

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef' | 'triggerRef' | 'open'> {
  onApply?: () => void
  onDecline?: () => void
}

function ExperiencesSearchDateSelectDropdown(props: Props) {
  const { anchorRef, triggerRef, open, onApply = noop, onDecline = noop } = props
  const { checkinDate, dateSearchOptionId } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const { searchLabel, clearAllLabel } = useExperienceSearchLabels()

  const unsetDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
  }, [globalSearchDispatch])

  const unsetFlexibleDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })
  }, [globalSearchDispatch])

  const selectAnytime = useCallback(() => {
    unsetDates()
    unsetFlexibleDates()
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.ANYTIME })
    globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: true })
  }, [globalSearchDispatch, unsetDates, unsetFlexibleDates])

  const isAnytime = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME && !checkinDate
  /** Check-in alone is enough; search applies the same day as check-out when checkout is unset. */
  const disableSearch = !checkinDate && !isAnytime

  return (
    <DropdownSheet
      data-testid="experiences-date-picker-dropdown"
      size="fill-anchor"
      open={open}
      anchorRef={anchorRef}
      triggerRef={triggerRef}
      primaryActionProps={{
        'data-testid': 'experiences-date-search-button',
        children: searchLabel,
        disabled: disableSearch,
        onClick: onApply,
      }}
      secondaryActionProps={{
        'data-testid': 'experiences-date-clear-all-button',
        kind: 'tertiary',
        children: clearAllLabel,
        onClick: selectAnytime,
      }}
      onClose={onDecline}
    >
      <VerticalSpacer gap={16}>
        <ExperiencesSearchDateSelectInline />
      </VerticalSpacer>
    </DropdownSheet>
  )
}

export default ExperiencesSearchDateSelectDropdown
